import { useState, useCallback, useRef, useEffect } from 'react';
import { getLeadDescription } from 'services/leadServices';
import { toastConstant } from 'constants/toastmessage';
import { useToast } from 'context/ToastContext';

// Global storage for descriptions across all cards (persists during scrolling)
const descriptionsCache = new Map();
const toggledDescriptionsSet = new Set();

/**
 * Custom hook to manage lead description state with persistence across react-window scrolling
 * @param {Object} item - The lead item
 * @returns {Object} - Description state and actions
 */
export const useLeadDescription = (item) => {
  const { addToast } = useToast();
  const itemPk = item?.pk;

  // Check if description is toggled (visible)
  const [showDescription, setShowDescription] = useState(toggledDescriptionsSet.has(itemPk));

  // Get cached description if available
  const [leadDescription, setLeadDescription] = useState(descriptionsCache.get(itemPk) || null);

  // Loading state
  const [isLoadingDescription, setIsLoadingDescription] = useState(false);

  // Track if we've tried to fetch this description
  const hasFetchedRef = useRef(descriptionsCache.has(itemPk));

  // Sync with global toggle state on mount/item change
  useEffect(() => {
    const isToggled = toggledDescriptionsSet.has(itemPk);
    setShowDescription(isToggled);
    setLeadDescription(descriptionsCache.get(itemPk) || null);
    hasFetchedRef.current = descriptionsCache.has(itemPk);
  }, [itemPk]);

  /**
   * Fetch description from API
   */
  const fetchDescription = useCallback(async () => {
    if (!item?.sk || hasFetchedRef.current) return;

    const leadSk = item?.sk.split('#');
    const leadId = leadSk[1];
    if (!leadId) return;

    setIsLoadingDescription(true);
    try {
      const isDraft = item?.is_draft || false;
      const response = await getLeadDescription(leadId, isDraft);

      const description = {
        content: response?.data?.leads_descrption || '',
        itemPk: itemPk,
      };

      // Store in cache and local state
      descriptionsCache.set(itemPk, description);
      setLeadDescription(description);
      hasFetchedRef.current = true;
    } catch (error) {
      console.error('Error fetching lead description:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.LEAD_DESCRIPTION_FETCH_FAILURE);

      // Store empty description to prevent refetching
      const emptyDescription = { content: '', itemPk: itemPk };
      descriptionsCache.set(itemPk, emptyDescription);
      setLeadDescription(emptyDescription);
      hasFetchedRef.current = true;
    } finally {
      setIsLoadingDescription(false);
    }
  }, [item, itemPk, addToast]);

  /**
   * Toggle description visibility and fetch if needed
   */
  const toggleDescription = useCallback(async () => {
    const newShowDescription = !showDescription;
    setShowDescription(newShowDescription);

    // Update global toggle state
    if (newShowDescription) {
      toggledDescriptionsSet.add(itemPk);

      // Fetch description if not already loaded/loading
      if (!leadDescription && !isLoadingDescription && !hasFetchedRef.current) {
        await fetchDescription();
      }
    } else {
      toggledDescriptionsSet.delete(itemPk);
    }
  }, [showDescription, itemPk, leadDescription, isLoadingDescription, fetchDescription]);

  return {
    showDescription,
    leadDescription,
    isLoadingDescription,
    toggleDescription,
  };
};

/**
 * Utility function to clear descriptions cache (useful for cleanup/refresh)
 */
export const clearDescriptionsCache = () => {
  descriptionsCache.clear();
  toggledDescriptionsSet.clear();
};

/**
 * Utility function to prefetch a description
 */
export const prefetchDescription = async (leadId, isDraft = false) => {
  try {
    const response = await getLeadDescription(leadId, isDraft);
    return response?.data?.leads_descrption || '';
  } catch (error) {
    console.error('Error prefetching description:', error);
    return '';
  }
};
